import Navigation from '@/components/Navigation'
import Footer from '@/components/Footer'
import { usePageMeta } from '@/hooks/usePageMeta'

const resources = [
  {
    name: 'First Responder Center for Excellence',
    logo: '/spoke-static/images/SPOKE_frce_logo.jpg',
    logoSize: 'h-40',
    logoBg: true,
    url: 'https://firstrespondercenter.org/',
    buttonLabel: 'Visit Website →',
    body: `The First Responder Center for Excellence (FRCE), an affiliate of the National Fallen Firefighters Foundation, is dedicated to protecting the lives and livelihoods of firefighters, EMS personnel, and other first responders through research, education, and training that reduce line-of-duty injuries, deaths, and occupational illnesses.\n\nPartnering with FRCE allows SPOKE to extend our film's message into real-world action by bringing powerful, story-driven content alongside FRCE's evidence-based resources, helping reach new audiences, spark conversation, and serve as a catalyst for culture change around first responder health and wellness.`,
  },
  {
    name: 'CRACKYL Magazine',
    logo: '/spoke-static/images/crackyl-logo.png',
    logoSize: 'h-20',
    logoBg: false,
    url: 'https://www.crackyl.com/',
    buttonLabel: 'Visit Website →',
    body: `CRACKYL Magazine is a firefighter-focused lifestyle and wellness publication dedicated to supporting the physical, mental, and emotional health of firefighters and their families.\n\nAs SPOKE's media partner, CRACKYL helps amplify important conversations around resilience, fitness, relationships, and life beyond the fireground, bringing our message directly into firehouses and firefighter communities across North America.`,
  },
  {
    name: 'First Responder Sleep Recovery',
    logo: '/spoke-static/images/frsr-logo-new.png',
    logoSize: 'h-40',
    logoBg: true,
    url: 'https://www.firstrespondersleeprecovery.com/band',
    buttonLabel: 'Visit Website →',
    body: `We are proud to have sleep and stress specialist Jacqueline Toomey in our documentary. Jacqueline is the founder of First Responder Sleep Recovery: a firefighter-owned training program that tackles the life-threatening impact of sleep loss on first responders.\n\nThrough nationally taught, comprehensive sleep-deprivation training, Jacqueline and her team serve fire, law enforcement, military, hospital, and EMS personnel with practical tools to restore rest, resilience, and long-term health.`,
  },
  {
    name: 'Pure Grit Performance',
    logo: '/spoke-static/images/pure-grit-logo.webp',
    logoSize: 'h-28',
    logoBg: false,
    url: 'https://www.puregritperformance.com/',
    buttonLabel: 'Visit Website →',
    body: `SPOKE features Pure Grit Performance founder, sports dietitian and coach Morgan Jarman. Morgan helps recreational, competitive, and tactical athletes build resilience through evidence-based nutrition, strength training, and mindset work.\n\nHer practice focuses on teaching people how to fuel their bodies to elevate both physical and mental performance without rigid, unsustainable rules, emphasizing grit, long-term goals, and sustainable habits rather than quick fixes.`,
  },
  {
    name: 'Renton Regional Fire Authority',
    logo: '/spoke-static/images/SPOKE_renton_fire_authority_logo.png',
    logoSize: 'h-44',
    logoBg: false,
    url: null,
    buttonLabel: null,
    body: `We're proud to partner with the Renton Regional Fire Authority (RRFA), whose dedicated F13 crew opened their doors to us during filming. Their teamwork, skill, and commitment to community safety embody the spirit of frontline service that our film celebrates.\n\nThrough our collaboration with RRFA, audiences gain a genuine look into the lives of the firefighters who protect and serve with courage every day.`,
  },
  {
    name: 'Pedals and Perseverance',
    logo: '/spoke-static/images/rudy-pospisil-book.jpg',
    logoSize: 'h-80',
    logoBg: false,
    url: 'https://www.amazon.com/Pedals-Perseverance-Firefighters-Survival-Inspiring/dp/1069125024',
    buttonLabel: 'Buy the Book →',
    body: `Meet Rudy, professional fire captain, lifelong athlete, and resilient cancer survivor. When life threw its toughest challenges his way, Rudy answered with strength, purpose and an unwavering desire to give back.\n\nDetermined to make a difference, he embarked on an extraordinary journey — riding his bicycle 18,000 miles around the world to raise awareness and crucial funds for cancer research.`,
  },
  {
    name: 'Filmabee Studios',
    logo: '/spoke-static/images/SPOKE_filmabee_studios_logo_transparent.png',
    logoSize: 'h-52',
    logoBg: false,
    url: 'https://filmabee.com',
    buttonLabel: 'Visit Website →',
    body: `SPOKE is a production of Filmabee Studios, and it sits right at the heart of the company's vision for cinema: intimate, human-centered stories that stay with you long after the credits roll.\n\nBy following real people through a descent "into maydays no one is talking about," SPOKE reflects Filmabee's belief that cinema can be a map for an inspired world, drawn from the real struggles and quiet courage of the people on screen. As a studio that "works with visionary film directors and their audience," Filmabee uses projects like SPOKE to turn lived experience into crafted, cinematic storytelling that sparks reflection and conversation, not just momentary spectacle.`,
  },
]

const ResourcesPage = () => {
  usePageMeta({
    title: 'Resources | SPOKE Film',
    description: 'Organisations, research, and resources for firefighter occupational cancer, PTSD, and first responder wellness.',
  })
  return (
    <div className="min-h-screen bg-[#0a0f1a] text-white">
      <Navigation />

      {/* Hero */}
      <section className="relative min-h-[38vh] bg-[#0a0f1a] flex items-end pt-24 pb-16 px-6">
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 to-[#0a0f1a]"></div>
        <div className="relative z-10 max-w-5xl mx-auto w-full text-center">
          <div className="w-12 h-1 bg-red-600 mb-6 mx-auto"></div>
          <h1 className="text-5xl md:text-7xl font-montserrat font-black uppercase tracking-wide text-white leading-none">
            Resources
          </h1>
          <p className="mt-4 text-yellow-400 font-poppins font-light text-lg">
            The people and organisations behind SPOKE — and where to go next.
          </p>
          <div className="mt-8">
            <a
              href="#support-resources"
              className="inline-flex items-center gap-2 border border-red-600 text-red-500 hover:bg-red-600 hover:text-white font-montserrat font-black uppercase tracking-widest text-xs px-6 py-3 transition-colors duration-200"
            >
              Immediate Support
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </a>
          </div>
        </div>
      </section>

      {/* Resource sections */}
      {resources.map((resource, i) => {
        const isEven = i % 2 === 0

        const LogoBlock = (
          <div className="flex items-center justify-center">
            {resource.logoBg ? (
              <div className="bg-white rounded-2xl p-6 inline-flex items-center justify-center transition-transform duration-200 hover:scale-110 cursor-pointer">
                <img
                  src={resource.logo}
                  alt={resource.name}
                  className={`${resource.logoSize} w-auto object-contain`}
                  loading="lazy"
                  decoding="async"
                />
              </div>
            ) : (
              <img
                src={resource.logo}
                alt={resource.name}
                className={`${resource.logoSize} w-auto object-contain transition-transform duration-200 hover:scale-110 cursor-pointer`}
                loading="lazy"
                decoding="async"
              />
            )}
          </div>
        )

        const TextBlock = (
          <div className="flex flex-col justify-center">
            <div className="flex items-center gap-3 mb-5">
              <div className={`w-8 h-[2px] ${isEven ? 'bg-red-600' : 'bg-yellow-400'}`}></div>
              <h2 className="font-montserrat font-black uppercase tracking-widest text-white text-sm">
                {resource.name}
              </h2>
            </div>
            {resource.body.split('\n\n').map((para, j) => (
              <p key={j} className="text-gray-300 font-poppins font-light text-base leading-relaxed mb-4">
                {para}
              </p>
            ))}
            {resource.url && (
              <a
                href={resource.url}
                target="_blank"
                rel="noopener noreferrer"
                className={`inline-block mt-2 text-xs font-montserrat font-black uppercase tracking-wider border px-5 py-2 transition-colors duration-200 ${isEven ? 'border-red-600 text-red-500 hover:bg-red-600 hover:text-white' : 'border-yellow-400 text-yellow-400 hover:bg-yellow-400 hover:text-black'}`}
              >
                {resource.buttonLabel}
              </a>
            )}
          </div>
        )

        return (
          <section
            key={resource.name}
            className={`py-[60px] md:py-20 px-6 ${isEven ? 'bg-black' : 'bg-[#0a0f1a]'}`}
          >
            <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-9 md:gap-20 items-center">
              {isEven ? (
                <>
                  <div>{LogoBlock}</div>
                  <div>{TextBlock}</div>
                </>
              ) : (
                <>
                  <div className="order-2 md:order-1">{TextBlock}</div>
                  <div className="order-1 md:order-2">{LogoBlock}</div>
                </>
              )}
            </div>
          </section>
        )
      })}

      {/* Support Resources */}
      <section id="support-resources" className="bg-[#0a0f1a] py-20 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="w-12 h-1 bg-red-600 mb-6"></div>
          <h2 className="font-montserrat font-black uppercase tracking-wide text-white text-3xl md:text-4xl mb-3">
            Support Resources
          </h2>
          <p className="font-poppins text-gray-400 text-base mb-12">
            If you or someone you know needs help, these organizations are here for you.
          </p>

          {/* Urgent — All First Responders */}
          <div className="mb-14">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-8 h-[2px] bg-red-600"></div>
              <h3 className="font-montserrat font-black uppercase tracking-widest text-red-500 text-sm">All First Responders — Immediate Support</h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                {
                  name: '988 Suicide & Crisis Lifeline',
                  desc: '24/7 national crisis support by call or text.',
                  contact: 'Call or text 988',
                  href: 'tel:988',
                  linkLabel: null,
                },
                {
                  name: 'Crisis Text Line',
                  desc: 'Immediate confidential text support for responders in crisis.',
                  contact: 'Text BADGE to 741741',
                  href: 'sms:741741&body=BADGE',
                  linkLabel: null,
                },
                {
                  name: 'Safe Call Now',
                  desc: 'Confidential crisis referral service for public safety employees and emergency services personnel.',
                  contact: '(206) 459-3020',
                  href: 'tel:2064593020',
                  linkLabel: null,
                },
                {
                  name: 'ResponderStrong',
                  desc: 'Mental health and wellness resources for emergency responders and their families.',
                  contact: null,
                  href: 'https://responderstrong.org',
                  linkLabel: 'responderstrong.org',
                },
                {
                  name: 'First Responder Support Network',
                  desc: 'Peer support and trauma recovery resources for first responders.',
                  contact: null,
                  href: 'https://www.frsn.org',
                  linkLabel: 'frsn.org',
                },
              ].map((r) => (
                <div key={r.name} className="border border-gray-800 p-6 hover:border-red-600 transition-colors duration-300">
                  <h4 className="font-montserrat font-black uppercase tracking-wide text-white text-sm mb-2">{r.name}</h4>
                  <p className="font-poppins text-gray-400 text-sm leading-relaxed mb-4">{r.desc}</p>
                  {r.contact && (
                    <a href={r.href} className="block font-montserrat font-black text-red-500 hover:text-red-400 text-sm mb-1 transition-colors">
                      {r.contact}
                    </a>
                  )}
                  {r.linkLabel && (
                    <a href={r.href} target="_blank" rel="noopener noreferrer" className="font-poppins text-red-500 hover:text-red-400 text-sm transition-colors underline underline-offset-2">
                      {r.linkLabel}
                    </a>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Fire / EMS / Dispatch */}
          <div className="mb-14">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-8 h-[2px] bg-yellow-400"></div>
              <h3 className="font-montserrat font-black uppercase tracking-widest text-yellow-400 text-sm">Fire / EMS / Dispatch</h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                {
                  name: 'Firefighter Behavioral Health Alliance',
                  desc: 'Behavioral health education, suicide-prevention awareness, and support resources for firefighters, EMS, and dispatch.',
                  href: 'https://www.ffbha.org',
                  linkLabel: 'ffbha.org',
                },
              ].map((r) => (
                <div key={r.name} className="border border-gray-800 p-6 hover:border-yellow-400 transition-colors duration-300">
                  <h4 className="font-montserrat font-black uppercase tracking-wide text-white text-sm mb-2">{r.name}</h4>
                  <p className="font-poppins text-gray-400 text-sm leading-relaxed mb-4">{r.desc}</p>
                  <a href={r.href} target="_blank" rel="noopener noreferrer" className="font-poppins text-yellow-400 hover:text-yellow-300 text-sm transition-colors underline underline-offset-2">
                    {r.linkLabel}
                  </a>
                </div>
              ))}
            </div>
          </div>

          {/* Law Enforcement */}
          <div>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-8 h-[2px] bg-gray-400"></div>
              <h3 className="font-montserrat font-black uppercase tracking-widest text-gray-300 text-sm">Law Enforcement</h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                {
                  name: 'CopLine',
                  desc: '24/7 confidential peer support line for active and retired law enforcement officers and their families.',
                  contact: '1-800-267-5463',
                  href: 'tel:18002675463',
                  linkLabel: 'copline.org',
                  linkHref: 'https://www.copline.org',
                },
              ].map((r) => (
                <div key={r.name} className="border border-gray-800 p-6 hover:border-gray-400 transition-colors duration-300">
                  <h4 className="font-montserrat font-black uppercase tracking-wide text-white text-sm mb-2">{r.name}</h4>
                  <p className="font-poppins text-gray-400 text-sm leading-relaxed mb-4">{r.desc}</p>
                  <a href={r.href} className="block font-montserrat font-black text-gray-300 hover:text-white text-sm mb-1 transition-colors">
                    {r.contact}
                  </a>
                  <a href={r.linkHref} target="_blank" rel="noopener noreferrer" className="font-poppins text-gray-400 hover:text-gray-200 text-sm transition-colors underline underline-offset-2">
                    {r.linkLabel}
                  </a>
                </div>
              ))}
            </div>
          </div>

        </div>
      </section>

      <Footer />
    </div>
  )
}

export default ResourcesPage
